import { useState, useEffect, useCallback, useMemo } from 'react';
import { targetTrackingApi, type TargetRow } from '../api/targetTracking';
import useAppStore from '../store/useAppStore';
import { MultiSelect, type SelectOption } from '../components/MultiSelect';
import { TableLoadingCell, TableLoadingOverlay } from '../components/TableLoadingOverlay';
import { sf, type FieldKey } from '../lib/fieldPolicy';
import { SortableHeader, sortRows, type SortState } from '../components/SortableTable';

type PeriodType = 'month' | 'quarter' | 'year';

interface PeriodOption { label: string; value: string; }

function generateMonthOptions(): PeriodOption[] {
  const opts: PeriodOption[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  for (let y = currentYear; y >= currentYear - 1; y--) {
    const maxM = y === currentYear ? currentMonth : 12;
    for (let m = maxM; m >= 1; m--) {
      const mm = String(m).padStart(2, '0');
      opts.push({ label: `${y}年${m}月`, value: `${y}-${mm}-01` });
    }
  }
  return opts;
}

function generateQuarterOptions(): PeriodOption[] {
  const opts: PeriodOption[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQ = Math.ceil((now.getMonth() + 1) / 3);
  for (let y = currentYear; y >= currentYear - 1; y--) {
    const maxQ = y === currentYear ? currentQ : 4;
    for (let q = maxQ; q >= 1; q--) {
      const mm = String((q - 1) * 3 + 1).padStart(2, '0');
      opts.push({ label: `${y}年第${q}季度`, value: `${y}-${mm}-01` });
    }
  }
  return opts;
}

function generateYearOptions(): PeriodOption[] {
  const opts: PeriodOption[] = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear; y >= currentYear - 2; y--) {
    opts.push({ label: `${y}年`, value: `${y}-01-01` });
  }
  return opts;
}

const fmtNum = (v: number) => v == null ? '-' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (v: number) => v == null ? '-' : v.toLocaleString('en-US');
const fmtPct = (v: number) => v == null ? '-' : (v * 100).toFixed(2) + '%';

interface ColDef {
  key: keyof TargetRow;
  label: string;
  fmt: (v: number) => string;
  permKey?: FieldKey;
}

const COLUMNS: ColDef[] = [
  { key: 'channel', label: '平台', fmt: (v) => String(v) },
  { key: 'person', label: '人员', fmt: (v) => String(v) },
  { key: 'spend', label: '花费', fmt: fmtNum, permKey: 'spend' },
  { key: 'impressions', label: '展示', fmt: fmtInt },
  { key: 'reach', label: '覆盖人数', fmt: fmtInt },
  { key: 'frequency', label: '频次', fmt: fmtNum },
  { key: 'clicks', label: '点击量', fmt: fmtInt },
  { key: 'add_to_cart', label: '加购数', fmt: fmtNum },
  { key: 'purchases', label: '购物', fmt: fmtNum },
  { key: 'revenue', label: '收入', fmt: fmtNum, permKey: 'revenue' },
  { key: 'registrations', label: '注册', fmt: fmtNum },
  { key: 'cpm', label: 'CPM', fmt: fmtNum, permKey: 'cpm' },
  { key: 'ctr', label: 'CTR', fmt: fmtPct },
  { key: 'cpc', label: 'CPC', fmt: fmtNum, permKey: 'cpc' },
  { key: 'add_to_cart_cost', label: '加购成本', fmt: fmtNum, permKey: 'cpa' },
  { key: 'cpa', label: 'CPA', fmt: fmtNum, permKey: 'cpa' },
  { key: 'roi', label: 'ROI', fmt: fmtNum, permKey: 'roas' },
  { key: 'aov', label: '客单价', fmt: fmtNum, permKey: 'revenue' },
  { key: 'add_to_cart_rate', label: '加购率', fmt: fmtPct },
  { key: 'cart_conversion_rate', label: '购物车转化率', fmt: fmtPct },
  { key: 'purchase_rate', label: '购买率', fmt: fmtPct },
];

type TargetMetricKey = Exclude<keyof TargetRow, 'channel' | 'person'>;

function isTargetMetricColumn(col: ColDef): col is ColDef & { key: TargetMetricKey } {
  return col.key !== 'channel' && col.key !== 'person';
}

/**
 * The platform column is visually merged. Keep its groups intact and only
 * order the people within each platform, so sorting a metric never breaks the
 * group/rowspan structure.
 */
function sortTargetRows(rows: TargetRow[], sort: SortState<TargetMetricKey>): TargetRow[] {
  if (!sort) return rows;

  const sorted: TargetRow[] = [];
  let start = 0;
  while (start < rows.length) {
    let end = start + 1;
    while (end < rows.length && rows[end].channel === rows[start].channel) end++;
    sorted.push(...sortRows(rows.slice(start, end), sort, (row, key) => row[key]));
    start = end;
  }
  return sorted;
}

function canSortTargetMetric(col: ColDef, userLevel: number) {
  return !col.permKey || sf('0', col.permKey, userLevel).display === 'show';
}

function computePlatformSpans(rows: TargetRow[]): number[] {
  const spans: number[] = new Array(rows.length).fill(1);
  let i = 0;
  while (i < rows.length) {
    let j = i + 1;
    while (j < rows.length && rows[j].channel === rows[i].channel) j++;
    spans[i] = j - i;
    for (let k = i + 1; k < j; k++) spans[k] = 0;
    i = j;
  }
  return spans;
}

const PERIOD_TABS: { key: PeriodType; label: string }[] = [
  { key: 'month', label: '月' },
  { key: 'quarter', label: '季' },
  { key: 'year', label: '年' },
];

export default function TargetTracking() {
  const { brandId, siteId, adContext } = useAppStore();
  const userLevel = adContext?.data_level ?? 1;
  const [periodType, setPeriodType] = useState<PeriodType>('month');
  const [periodStarts, setPeriodStarts] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TargetRow[]>([]);
  const [sort, setSort] = useState<SortState<TargetMetricKey>>(null);

  const monthOptions = useMemo(() => generateMonthOptions(), []);
  const quarterOptions = useMemo(() => generateQuarterOptions(), []);
  const yearOptions = useMemo(() => generateYearOptions(), []);

  const isMultiPeriod = periodType === 'month' || periodType === 'quarter';

  useEffect(() => {
    if (periodStarts.length === 0) {
      const src = periodType === 'month' ? monthOptions
        : periodType === 'quarter' ? quarterOptions
        : yearOptions;
      if (src.length > 0) setPeriodStarts([src[0].value]);
    }
  }, [periodType, monthOptions, quarterOptions, yearOptions, periodStarts.length]);

  const handlePeriodChange = useCallback((p: PeriodType) => {
    setPeriodType(p);
    setPeriodStarts([]);
  }, []);

  const fetchData = useCallback(async () => {
    if (periodStarts.length === 0) return;
    setLoading(true);
    try {
      const res = await targetTrackingApi.getData({
        brand_id: brandId,
        site_id: siteId || undefined,
        period_type: periodType,
        period_start: periodStarts.join(','),
      });
      setRows(res.data ?? []);
    } catch (e) {
      console.error('Failed to load target tracking data:', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [brandId, siteId, periodType, periodStarts]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sortedRows = useMemo(() => sortTargetRows(rows, sort), [rows, sort]);
  const platformSpans = useMemo(() => computePlatformSpans(sortedRows), [sortedRows]);

  const toggleSort = useCallback((key: TargetMetricKey) => {
    setSort(current => {
      if (!current || current.key !== key) return { key, direction: 'asc' };
      if (current.direction === 'asc') return { key, direction: 'desc' };
      return null;
    });
  }, []);

  const currentSelectOptions: SelectOption[] = useMemo(() => {
    const src = periodType === 'month' ? monthOptions
      : periodType === 'quarter' ? quarterOptions
      : yearOptions;
    return src.map(o => ({ value: o.value, label: o.label }));
  }, [periodType, monthOptions, quarterOptions, yearOptions]);

  const cellVal = (col: ColDef, row: TargetRow) => {
    if (col.key === 'channel' || col.key === 'person') return row[col.key];
    const raw = col.fmt(row[col.key] as number);
    return col.permKey ? (sf(raw, col.permKey, userLevel).value ?? '🔒') : raw;
  };

  return (
    <div className="page-section visible">
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end',
        padding: '14px 16px',
        background: 'var(--surface)', borderRadius: 'var(--radius-sm)',
        border: '1px solid var(--border2)', marginBottom: 12,
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>时间维度</label>
          <div className="page-tabs" style={{ marginBottom: 0 }}>
            {PERIOD_TABS.map(p => (
              <div key={p.key} className={`page-tab${periodType === p.key ? ' active' : ''}`} onClick={() => handlePeriodChange(p.key)}>
                {p.label}
              </div>
            ))}
          </div>
        </div>

        {currentSelectOptions.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 220 }}>
            <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text3)' }}>
              {periodType === 'month' ? '选择月' : periodType === 'quarter' ? '选择季度' : '选择年'}
            </label>
            <MultiSelect
              single={!isMultiPeriod}
              searchable
              options={currentSelectOptions}
              value={periodStarts}
              onChange={v => setPeriodStarts(isMultiPeriod ? v : v.length > 0 ? [v[0]] : [])}
              placeholder="请选择"
            />
          </div>
        )}
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <div className="card-title">目标跟进</div>
        </div>
        <div style={{ position: 'relative', overflowX: 'auto' }}>
          <table style={{ minWidth: 1200 }}>
            <thead>
              <tr>
                {COLUMNS.map(c => (
                  isTargetMetricColumn(c) ? (
                    <SortableHeader
                      key={c.key}
                      sortKey={c.key}
                      sort={sort}
                      onSort={toggleSort}
                      align="right"
                      disabled={!canSortTargetMetric(c, userLevel)}
                    >
                      {c.label}
                    </SortableHeader>
                  ) : (
                    <th key={c.key}>{c.label}</th>
                  )
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr><td colSpan={COLUMNS.length}><TableLoadingCell /></td></tr>
              ) : sortedRows.length === 0 ? (
                <tr><td colSpan={COLUMNS.length} style={{ textAlign: 'center', padding: 32, color: 'var(--text3)' }}>暂无数据</td></tr>
              ) : sortedRows.map((row, idx) => (
                <tr key={row.channel + row.person + idx}>
                  {COLUMNS.map(col => {
                    if (col.key === 'channel' && platformSpans[idx] === 0) return null;
                    const extraStyle: React.CSSProperties = {};
                    if (col.key === 'channel' && platformSpans[idx] > 1) {
                      extraStyle.verticalAlign = 'middle';
                      extraStyle.fontWeight = 600;
                    }
                    if (col.key !== 'channel' && col.key !== 'person') {
                      extraStyle.textAlign = 'right';
                      extraStyle.fontVariantNumeric = 'tabular-nums';
                    }
                    return (
                      <td
                        key={col.key}
                        rowSpan={col.key === 'channel' ? (platformSpans[idx] || undefined) : undefined}
                        style={Object.keys(extraStyle).length > 0 ? extraStyle : undefined}
                      >
                        {cellVal(col, row)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {loading && rows.length > 0 && <TableLoadingOverlay />}
        </div>
      </div>
    </div>
  );
}
